import type { Urls } from 'Common/Urls';
import { walkObjects } from '../canvas/utils';
import { collectUsedFamilies } from '../fonts/fonts';
import { fontEpoch, geometryReady, watchFontLoads } from '../canvas/text/fitText';
import { effectiveImageSrc } from '../canvas/images/effectiveSrc';
import type { RuntimeImage, RuntimeObject, DataRecord } from '../types';
import type { ThemeRecord } from './themeVariables';

/*
	Whether the poster can be captured to a JPEG that matches what the user sees. Capturing an
	unbuildable poster bakes in whatever the canvas is showing at the time — a stripe placeholder
	where an image should be, or fallback-font metrics — so the render is blocked until it can be
	done faithfully, and the event page falls back to a generated poster in the meantime.
*/
export type UnbuildableReason = 'images' | 'fonts';

export function unbuildableReason(
	objects: RuntimeObject[],
	record: DataRecord,
	theme: ThemeRecord | undefined,
	urls: Pick<Urls,'imageUrl'>,
	proxyProductionAssets: boolean,
): UnbuildableReason | undefined
{
	if (anyImageMissing(objects, record, theme, urls, proxyProductionAssets))
		return 'images';
	if (!posterFontsLoaded(objects, theme))
		return 'fonts';
	return undefined;
}

export function unbuildableMessage(reason: UnbuildableReason): string
{
	return reason == 'images'
		? 'Unable to build the poster due to missing images. A fallback will be used.'
		: 'Unable to build the poster until its fonts have loaded. A fallback will be used.';
}

/* An image object that resolves to no source at all renders the diagonal-stripe placeholder,
   which is never worth capturing. */
export function anyImageMissing(
	objects: RuntimeObject[],
	record: DataRecord,
	theme: ThemeRecord | undefined,
	urls: Pick<Urls,'imageUrl'>,
	proxyProductionAssets: boolean,
): boolean
{
	return walkObjects(objects, obj =>
		obj.type == 'image' && !effectiveImageSrc(obj as RuntimeImage, record, theme, urls, proxyProductionAssets)
	);
}

/* Every web font the poster's objects use has arrived. Gated on geometryReady() as well as the
   per-family check: document.fonts.check() reports a family with no @font-face rule yet as
   available, so before FontLoader's <link>s register it would claim a font that is still on its
   way. Reads fontEpoch to re-run as each font lands. */
export function posterFontsLoaded(objects: RuntimeObject[], theme: ThemeRecord | undefined): boolean
{
	watchFontLoads();
	fontEpoch();
	if (!geometryReady())
		return false;
	return [...collectUsedFamilies(objects, theme)].every(family => document.fonts.check(`16px "${family}"`));
}
